import { AuthorActivity } from "@components/library/AuthorActivity";
import { EntityIcon } from "@components/library/Entities/EntityIcon";
import dayjs from "dayjs";
import { File } from "@/types/app/file";
import { VersionDescription } from "@components/library/Entities/shared/EntityPopover";
import { DefaultEnvironmentTag } from "@components/PromptEditor/VariableTags";
import Button from "@components/library/Button";
import Tag from "@components/library/v2/Tag";
import { useState } from "react";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { useFile } from "@/services/files.service";
import { hlToast, ToastVariant } from "@components/library/Toast";

interface Props {
  fileId: string;
  versionId: string;
}

const VersionDrawerHeader = ({ fileId, versionId }: Props) => {
  const { file: version, mutate } = useFile({ fileId, versionId });
  const [showAllEnvironments, setShowAllEnvironments] = useState(false);

  if (version === undefined) {
    return null;
  }

  const fileType = version.type;

  // Sort environments to prioritize default environments
  const sortedEnvironments = [...version.environments].sort((a, b) => {
    if (a.tag === "default") return -1;
    if (b.tag === "default") return 1;
    return a.name.localeCompare(b.name);
  });

  // Define how many environments to show in collapsed state
  const initialDisplayCount = 2;
  const hasMoreEnvironments = sortedEnvironments.length > initialDisplayCount;

  return (
    <div className="flex w-full min-w-0 flex-col  items-start gap-20">
      <div className="flex h-full w-full  min-w-0 items-start justify-between">
        <div className="flex items-center gap-8">
          <EntityIcon size={20} type={fileType} />

          <div className="max-w-[350px] truncate pr-8 text-18-24 font-bold" title={version.name}>
            {version.name}
          </div>
          <FileLabelWithPopover
            fileId={fileId}
            versionId={versionId}
            size={24}
            onClick={() => {
              navigator.clipboard.writeText(versionId);
              hlToast({
                title: "Copied Version ID to clipboard",
                variant: ToastVariant.Success,
              });
            }}
            versionPopoverProps={{
              showOpenButton: false, // Hide the open drawer button in the header, since we're already in the drawer
            }}
          />
        </div>

        {sortedEnvironments.length > 0 && (
          <div className="flex min-w-0 items-end gap-8">
            <div className="flex max-w-[450px] flex-wrap items-center justify-end gap-8">
              {/* Always show either the initial set or all environments */}
              {(showAllEnvironments ? sortedEnvironments : sortedEnvironments.slice(0, initialDisplayCount)).map(
                (env) =>
                  env.tag === "default" ? (
                    <DefaultEnvironmentTag size={24} key={env.id} name={env.name} />
                  ) : (
                    <Tag size={24} key={env.id}>
                      <span className="text-12-14 font-medium text-text-base-1">{env.name}</span>
                    </Tag>
                  ),
              )}

              {/* Toggle button that's always in the same position */}
              {hasMoreEnvironments && (
                <Button size={24} styling="outline" onClick={() => setShowAllEnvironments(!showAllEnvironments)}>
                  {showAllEnvironments ? "Show less" : `+${sortedEnvironments.length - initialDisplayCount}`}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex w-full flex-col items-stretch gap-12 ">
        {version.version_description && (
          // TODO: Make editable
          <VersionDescription hasOwner={true} size="lg">
            <span className="text-14-20 ">{version.version_description}</span>
          </VersionDescription>
        )}

        <div className="flex items-center gap-12">
          <AuthorActivity.Root>
            {version.created_by && <AuthorActivity.Avatar user={version.created_by} />}
            <AuthorActivity.Text>
              {version.created_by && <AuthorActivity.Name>{version.created_by.full_name}</AuthorActivity.Name>}
              <AuthorActivity.ActionAndTimestamp
                action={version.created_by ? "created" : "Created"}
                timestamp={dayjs.utc(version.created_at).local()}
              />
            </AuthorActivity.Text>
          </AuthorActivity.Root>
        </div>
      </div>
    </div>
  );
};

export default VersionDrawerHeader;
